/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  addIframeSrcParams,
  addQueryParam,
  cleanQuery,
  getPaths,
  getPathWithoutTenant,
  getQueryParams,
  getQueryValue,
  getTenantAndPath,
  getUpdatedPath,
  hasChanges,
  hashToPath,
  splitPath,
  updatePath
} from '@exc/url';
import BrandIcons from 'url:@exc/brand-icons/BrandIcons.svg';
import classNames from 'classnames';
import CoreContext from '../context';
import {debounce, getValidSourcesRegexString, isOrgOrSubOrgChange, waitForGainsightCallback} from '../utils';
import {ErrorTypes, HTMLStatus} from '../enums';
import eventObserver from './EventObserver';
import {Events, EventsThirdParty} from '@exc/shared';
import {getConfiguration} from '../utils/config';
import {history} from '@exc/router';
import Iframe from './Iframe';
import {Internal} from '@adobe/exc-app/internal';
import metrics, {Level, Events as MetricsEvents} from '@adobe/exc-app/metrics';
import PropTypes from 'prop-types';
import React from 'react';
import {RUNTIME} from '../models/Solution';
import ShellWait from '@exc/shell/src/ShellWait';
import './Sandbox.css';

const scopedEvents = {
  helpCenter: Events.CUSTOM_HELP_BUTTON_CLICK,
  helpCenterResource: Events.CUSTOM_HELP_BUTTON_CLICK,
  userProfile: Events.CUSTOM_PROFILE_BUTTON_CLICK
};

const ErrorView = React.lazy(() => import('./Error'));
const Heading = React.lazy(() => import('@react/react-spectrum/Heading'));

export default class Sandbox extends React.Component {
  static lastAppId;
  static firstRender = true;

  constructor(props, context) {
    super(props, context);

    const {analytics, appId, metricsAppId, sandbox, sections} = props.config;
    // Trigger gainsight callback when it's available with the appId of the
    // application that is loading.
    if (analytics && analytics.code) {
      waitForGainsightCallback().then(callback => callback(analytics.code));
    }

    let env = context.environment;
    const {history, ims, thirdParty} = sandbox;
    const {type, config} = history || {};
    const queryParams = getQueryParams();

    // Add the module runtime src
    this.runtimeScript = document.getElementById('runtimeScript');
    if (!this.runtimeScript) {
      throw new Error('Missing Module Runtime src');
    }
    this.appDone = false;
    this.thirdParty = thirdParty;
    this.waitForRuntime = false;
    this.waitForDiscovery = false;
    this.waitForReady = false;
    this.discoveryPending = false;

    // Debounce the history message so that we can wait for additional updates
    // from the framed application so that we know we're sending to the right
    // place. Additionally, this should solve issues around navigation loops
    // because it's letting the app send all the events it needs.
    this.sendHistoryMessage = debounce((...args) => this.sendMessage(...args), 100);

    this.getPulseService = async () => {
      const {default: {getPulseService}} = await import('@exc/pulse-sdk');
      const {imsToken: accessToken} = this.context;
      return getPulseService({authInfo: {accessToken}, env, queryParams});
    };
    this.metrics = metrics.create('exc.core.modules.Sandbox');
    this.context.setSolutionConfig(props.config);

    if (ims) {
      this.customIms = ims[env] || {
        client_id: ims.client_id,
        scope: ims.scope
      };
      this.getToken(this.customIms);
    }
    this.unloadPromptMessage = this.context.formatMessage({
      customLocale: this.context.locale,
      defaultMessage: 'Are you sure?',
      messageId: 'unloadPromptMessage'
    });

    this.appId = appId;
    this.metricsAppId = metricsAppId || appId;
    this.sections = sections;

    // If the user switched from app A to app B, and app A did not call "page.done",
    // we want to turn firstRender off so we don't log performance metrics for app B.
    if (Sandbox.lastAppId && Sandbox.lastAppId !== appId) {
      Sandbox.firstRender = false;
      this.lastAppId = Sandbox.lastAppId;
      this.sandboxStart = performance.now();
    }
    Sandbox.lastAppId = appId;

    this.connected = false;
    this.frame = null;
    this.mounted = true;
    this.historyConfig = config || {};
    this.historyType = type || history;
    this.initialized = false;
    this.lastPayload = null;
    this.outQueue = [];
    this.paramsChangedOnUpdate = false;
    this.onMessage = this.onMessage.bind(this);
    this.onMount = this.onMount.bind(this);
    this.openRequests = {};
    this.queuedDataEvents = [];
    this.state = {
      discovery: false,
      error: null,
      frameKey: 0,
      loaded: false,
      main: false,
      showWaitMessage: false,
      spinner: !sandbox.hideInitialSpinner,
      src: null
    };
    this._discoveryTime = 0;

    this.sandboxTimer = this.metrics.start('Sandbox', {applicationId: this.metricsAppId});
    eventObserver.on('keyboard', this.handleUserInputEvent);
    eventObserver.on('click', this.handleUserInputEvent);
    eventObserver.on('mouseup', this.handleUserInputEvent);
    const {endpoints} = getConfiguration({environment: this.context.environment});
    this.apiGatewayUrl = endpoints.apiGateway;
    this.ioGatewayUrl = endpoints.ioGateway;
    this.ioRegionSpecificMap = endpoints.regionGatewayIoMap;
    this.xqlGatewayUrl = endpoints.xql;
    // Reset skip menu when new sandbox is constructed
    document.dispatchEvent(new CustomEvent('skipMenuList', {detail: null}));
    document.addEventListener('getSkipMenu', this.fetchSkipMenu);

    this.setPageTimeout();
    this.validSourcesRegexString = getValidSourcesRegexString();
  }

  async setPageTimeout() {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      delete this.loadTimeout;
    }
    // unifiedShellConfig is set in the browser by a user so it is not part of the storage package
    const localConfigs = JSON.parse(window.localStorage.getItem('unifiedShellConfig')) || {};
    const devmodeEnabled = localConfigs.devmodeEnabled === true || getQueryValue('devmode') === 'true';
    if (this.initialized || this.context.environment === 'dev' || devmodeEnabled) {
      clearTimeout(this.loadMessageTimeout);
      delete this.loadMessageTimeout;
      return;
    }

    const {pageTimeout} = this.props.config.sandbox;
    const shortTimeoutEnvs = ['stage', 'prod'];
    const DEFAULT_TIMEOUT = shortTimeoutEnvs.includes(this.context.environment) ? 25000 : 90000;
    const PAGE_TIMEOUT = pageTimeout || DEFAULT_TIMEOUT;
    const LOAD_MESSAGE_TIMEOUT = 15000;

    const getTimeoutDiagnostics = () => {
      const {src} = this.state;
      const {availability = {}, getIframeEntry, nonSupportedBrowser} = this.context;
      // Log the iframe HTML resource entry if available.
      // This indicates the iframe HTML was downloaded successfully.
      const entry = getIframeEntry && src && getIframeEntry(this.iframeStartTime, src.toString());
      return {
        appId: this.metricsAppId,
        connected: this.connected,
        cookie: document.cookie.length,
        entry,
        hasFrame: !!this.frame,
        nonSupportedBrowser,
        url: src,
        waitForContext: !!this.waitingForContextUpdates,
        waitForDiscovery: this.waitForDiscovery,
        waitForReady: this.waitForReady,
        waitForRuntime: this.waitForRuntime,
        ...availability
      };
    };

    // A timeout that shows an additional message to the user that says the page
    // is loading slowly but give it more time. This will only update the UI
    // if there aren't other issues going on.
    this.loadMessageTimeout = this.loadMessageTimeout || setTimeout(() => {
      if (this.state.error || this.state.notFound || !this.mounted) {
        // If we know there was a valid reason for the iframe not to load, avoid timeout.
        return;
      }
      this.metrics.event(
        'Sandbox.pageIsSlow',
        `Solution loading is slow and message shown to user at ${LOAD_MESSAGE_TIMEOUT}ms`,
        {level: Level.WARN},
        getTimeoutDiagnostics()
      );
      this.setState({showWaitMessage: true});
    }, LOAD_MESSAGE_TIMEOUT);

    // The 408 timeout that will set the timeout message when it fires unless
    // there is a more nefarious reason that it fired (e.g. error, the frame
    // wasn't mounted, etc.)
    this.loadTimeout = setTimeout(() => {
      if (this.state.error || this.state.notFound || !this.mounted) {
        // If we know there was a valid reason for the iframe not to load, avoid timeout.
        return;
      }
      this.setState({error: ErrorTypes.TIMEOUT});
      this.metrics.event(
        'Sandbox.pageTimeout',
        `Solution loading timed out at ${PAGE_TIMEOUT}ms`,
        {level: Level.ERROR},
        getTimeoutDiagnostics()
      );
    }, PAGE_TIMEOUT);
  }

  /**
   * Determines whether we should wait for context updates specifically
   * required by solutions using discovery and url contexts such as sandbox
   * and suborgs.
   * @param {Object} src Discovery src object from the solution config.
   */
  shouldWaitForUrlContextValues(src = {}) {
    const {payload = []} = src;
    return payload.some(key => key in this.context && !this.context[key]);
  }

  /**
   * Determines whether we should wait for context updates to load the iframe.
   * This happens in the following cases:
   *  1. We want to delay discovery until we know that the org is permissioned on the solution.
   *  2. We want to delay discovery until we have all the url context information.
   *  3. IMS profile is required for discovery calls
   *  4. Access flag is defined in solution config, so feature flags are necessary
   *  5. SPA apps with AB testing enabled
   */
  shouldWaitForContextUpdates(src) {
    const {discoveryService, featureFlags, imsProfile, orgPermissioned} = this.context;
    let {access: {flag} = {}, spaAppId} = this.props.config;
    const discoveryUrl = typeof src === 'object';
    const urlNeedsImsInfo = discoveryUrl || (typeof src === 'string' && src.match(/{[A-Za-z]+}/g));
    return (discoveryUrl && (!orgPermissioned || !discoveryService || this.shouldWaitForUrlContextValues(src))) ||
      (urlNeedsImsInfo && !imsProfile) ||
      (flag && !featureFlags) ||
      (this.getSpaApp(spaAppId).ABTesting && !featureFlags);
  }

  getAppEnvironment = () => getQueryValue('source') || this.context.environment;

  getSourceEnv() {
    const env = this.getAppEnvironment();
    const {appId, spaAppId, sandbox} = this.props.config;
    const {pathPrefix} = sandbox;
    let srcEnv = sandbox.sources[env];

    // If SpaAppId exists, set the config URL for the respective environment
    if (env && spaAppId && !srcEnv) {
      srcEnv = `${this.context.cdn}/solutions/${spaAppId}${pathPrefix || ''}`;
    } else if (srcEnv && spaAppId && pathPrefix && env === 'dev') {
      srcEnv += pathPrefix;
    }

    if (!env || !srcEnv) {
      this.metrics.error(`No matching configuration source for env ${env} in app ${appId}`);
      this.setState({noSource: true, notFound: true});
      return;
    }
    return srcEnv;
  }

  /**
   * Updates digitalData (OMEGA) with the current active solution and version.
   * @param {string=} version Application version.
   */
  updateDigitalData(version) {
    const {code} = this.props.config.analytics || {};
    // Builds the digital data object in case it doesn't exist yet.
    ['digitalData', 'page', 'solution'].reduce((obj, key) => {
      obj[key] = obj[key] || {};
      return obj[key];
    }, window);
    const solution = {name: code || 'exc'};
    version && (solution.version = version);
    Object.assign(window.digitalData.page.solution, solution);
  }

  updateSource(discovery, src) {
    // If we don't have a legit source, send diagnostic info to EIM.
    (!src || !src.toString().startsWith('http')) && this.metrics.event(
      'Sandbox.invalidSource',
      {level: Level.ERROR},
      {
        appId: this.metricsAppId,
        nonSupportedBrowser: this.context.nonSupportedBrowser,
        src
      });
    this.iframeStartTime = performance.now();
    // TODO: Set Not Found or Error state if can't get a source.
    // This should be done in ticket EXC-14593 after we verify there is no
    // legit reason for source to be empty here.
    return this.setState({discovery, src});
  }

  setMetricsApplication(spaApp) {
    // Set the application in metrics
    const solutionConfig = {id: this.metricsAppId};
    spaApp && Object.assign(solutionConfig, {version: spaApp.liveVersion});
    Internal.setApplication({solution: solutionConfig});
  }

  getDiscoveryOptions() {
    const {appId, sections, serviceCode, session: sessionConfig} = this.props.config;
    return {
      appId,
      basePath: this.basePath,
      sections,
      serviceCode,
      sessionConfig
    };
  }

  async generateIframeSource(forceReload) {
    this.updateBasePath(this.props);
    const {spaAppId} = this.props.config;
    const {absolutePaths, hashType} = this.historyConfig;
    const isHashType = this.historyType === Sandbox.HISTORY_TYPES.HASH;
    const pathType = isHashType ? 'hash' : 'pathname';

    const {host, origin} = window.location;
    let src = this.getSourceEnv();

    if (!src) {
      return;
    }

    // Waiting for IMS to return in the case of a discovery URL.
    // In the case of a regular source, let's proceed!
    if (this.shouldWaitForContextUpdates(src)) {
      this.waitingForContextUpdates = true;
      return;
    }

    let discovery;
    let discoveryHasUrl = false;
    let fromDiscovery = false;
    let iframeTimer = this.metrics.start('IframeTimer');

    if (typeof src === 'string') {
      src = new URL(this.updateTemplatedURL(src));
    } else if (typeof src === 'object') {
      // Restart the timeout countdown for discovery
      this.setPageTimeout();
      fromDiscovery = true;
      try {
        this.waitForDiscovery = true;
        const {discoveryService} = this.context;
        src = {...src, discovery: this.updateTemplatedURL(src.discovery)};
        discovery = await discoveryService.getSourceFromDiscovery(src, this.context, this.getDiscoveryOptions());
        this.waitForDiscovery = false;

        if ('url' in discovery) {
          discoveryHasUrl = true;
          src = new URL(discovery.url);
        } else if ('url' in src) {
          src = new URL(this.updateTemplatedURL(src.url, {...src, ...discovery}));
        } else {
          throw new Error('Discovery must either return a URL, or contain a URL in the configuration');
        }

        // Custom IMS client and scope is returned!
        if (discovery.client_id && discovery.scope) {
          this.customIms = {
            client_id: discovery.client_id,
            scope: discovery.scope
          };
        }
        this.metrics.event('Sandbox.discoveryURL', {discovery, src});
      } catch (error) {
        this.waitForDiscovery = false;
        if (this.mounted) {
          this.metrics.event('Sandbox.discoveryFailed', {appId: this.metricsAppId, message: error.message}, {level: Level.ERROR});
          this.setState({noSource: true, notFound: true});
        }
        return;
      }

      this.setPageTimeout();
    }
    this.configURL = new URL(src);
    this.origin = src.origin;

    // SPA Pipeline apps should always get Runtime from the same domain as Shell.
    this.runtimeUrl = this.getRuntimeUrl();
    addQueryParam(src.searchParams, '_mr', this.runtimeUrl.toString());
    addQueryParam(src.searchParams, 'shell_domain', host);

    let spaApp;
    // Gets the spaApp and gets the liveVersion from the window config or an
    // enabled feature flag. If no liveVersion is available, don't use it.
    if (spaAppId) {
      spaApp = this.getSpaAppConfig(spaAppId);
      spaApp.liveVersion || (spaApp = null);
    }

    // Set the application in metrics
    this.setMetricsApplication(spaApp);

    // If discovery was used and is not from the cache, we should have the
    // entire URL, so we can just set the src and stop. If the discovery
    // response didn't have the url (came from config), or if the discovery came
    // from cache, we need to pull all the path and query params from the
    // current URL so that it's on the correct page.
    if (discovery && discoveryHasUrl) {
      forceReload && src.searchParams.set('shell_forceReload', Date.now());
      this._discoveryTime = iframeTimer.time('Loaded.Discovery.NotCache', {url: src});
      // Update the digital data for the current app.
      this.updateDigitalData();
      return this.updateSource(fromDiscovery, src);
    }

    addIframeSrcParams(src, this.runtimeUrl, spaApp);

    // Update the digital data for the current app.
    this.updateDigitalData(spaApp && spaApp.liveVersion);

    // Append the correct path from the outer frame (useful for deep linking)
    // This should correct to the "right" url (eg the page that is initially
    // loaded) if our src is just the base (eg '/').
    const params = this.props.params[0] || '';
    if (params) {
      const path = hashType === 'noslash' ? params : `/${params}`;
      if (absolutePaths) {
        src[pathType] = path;
      } else {
        src[pathType] += path;
      }
      // Replacing any duplicate slashes (e.g. //something) with a single slash
      // so that the URL is not broken.
      src[pathType] = src[pathType].replace(/([/]+)/g, '/');
    }
    // Pass all non-config query params through to the iframe.
    const url = new URL(`${origin}${hashToPath()}`);
    // If the history type isn't hash, the hash should be set.
    if (!isHashType) {
      src.hash = url.hash;
    }

    this.origin = src.origin;

    forceReload && src.searchParams.set('shell_forceReload', Date.now());
    this._discoveryTime = iframeTimer.time('Loaded', {url: src});
    this.updateSource(fromDiscovery, src);

    // If app not yet initialized, start countdown again
    this.setPageTimeout();
  }

  getSpaApp = spaAppId => ((window.config.solutions || {})[spaAppId] || {})

  /**
   * Gets the config for the SPA app by id. It uses the window.config.solutions
   * object to get the base config and uses feature flags to get a specific
   * version, if available.
   * @param {string} spaAppId The AppId of the SPA-Pipeline SPA.
   * @returns {Object} App config if set, undefined otherwise.
   */
  getSpaAppConfig(spaAppId) {
    const {featureFlags = {}} = this.context;
    const featureKey = `${spaAppId}_controlrollout`;
    const spaApp = Object.assign({}, this.getSpaApp(spaAppId), {spaName: spaAppId});

    // If the AB testing flag isn't set, the feature flag isn't available for
    // the current spaApp, or the flag exists but is set to the LIVEVERSION
    // string, use the configuration provided by the content server.
    if (!spaApp.ABTesting || !(featureKey in featureFlags) || featureFlags[featureKey] === 'LIVEVERSION') {
      return spaApp;
    }

    // Store the version from HTML config for more diagnostic information.
    const configVersion = spaApp.liveVersion;
    // Use the liveversion from the feature flag.
    spaApp.liveVersion = featureFlags[featureKey];
    this.metrics.info(`${spaAppId} is using AB testing`, {
      configVersion,
      liveVersion: spaApp.liveVersion,
      spaAppId
    });
    return spaApp;
  }

  updateBasePath(props) {
    let {params, path} = props;
    let {path: justPath, tenant} = getTenantAndPath(path);
    this.basePath = justPath.replace(params[0] || '', '')
      .replace(/\/$/, '') // Remove trailing slash
      .replace(/\/{2,}/, '/'); // Remove any double slashes
    // Ensure correct base path if switching between tenanted and tenantless
    if (tenant && !this.props.config.hideTenant) {
      this.basePath = `/@${tenant}${this.basePath}`;
    }
    this.context.setConfigStates({basePath: this.basePath});
  }

  updateTemplatedURL(urlString, extraParams = {}) {
    const templateStrings = urlString.match(/{[A-Za-z0-1]+}/g);
    const url = new URL(window.location.href);
    if (templateStrings) {
      templateStrings.forEach(string => {
        let contextString = string.substring(1, string.length - 1);
        let replacement =
          this.context[contextString] ||
          url.searchParams.get(contextString) ||
          this.props.params[contextString] ||
          extraParams[contextString] ||
          (extraParams.defaults || {})[contextString];
        if (replacement) {
          urlString = urlString.replace(string, replacement);
        }
      });
    }
    return urlString;
  }

  getNextConfig() {
    const {hash} = window.location;
    const match = this.props.router.match(hashToPath({hash}));
    if (!match) {
      return false;
    }
    const children = (match.props || {}).children;
    if (!children) {
      return undefined;
    }
    return (children.props || {}).config;
  }

  componentWillUnmount() {
    const {appId} = this.props.config;
    const nextAppId = (this.getNextConfig() || {}).appId;
    const nextSideNav = (this.getNextConfig() || {}).sideNav;

    eventObserver.off('keyboard', this.handleUserInputEvent);
    eventObserver.off('click', this.handleUserInputEvent);
    eventObserver.off('mouseup', this.handleUserInputEvent);
    document.removeEventListener('getSkipMenu', this.fetchSkipMenu);

    this.frame = null;
    this.mounted = false;
    this.customFeedback = false;

    this.context.resetConfigStates(appId, nextAppId, nextSideNav);
    this.blockNavigation(false);
    clearTimeout(this.loadMessageTimeout);
    delete this.loadMessageTimeout;
    clearTimeout(this.loadTimeout);
  }

  getRuntimeUrl() {
    const {runtime, spaAppId} = this.props.config;
    const useCDN = spaAppId || runtime === RUNTIME.CDN;
    const runtimeOrigin = useCDN ? this.context.cdn : origin;
    return new URL(this.runtimeScript.getAttribute('data-src'), runtimeOrigin);
  }

  async generateSourceOnReload() {
    const src = this.getSourceEnv();

    if (!src || this.shouldWaitForContextUpdates(src)) {
      src && (this.waitingForContextUpdates = true);
      return;
    }

    this.generateIframeSource();
  }

  componentDidMount() {
    this.generateSourceOnReload();
  }

  shouldComponentUpdate(nextProps) {
    const getParam = p => (Object.keys(p).length ? p[0] : '') || '';

    const {hash, params} = nextProps;
    const query = cleanQuery(nextProps.query);
    const nextParams = getParam(params);
    const prevParams = getParam(this.props.params);
    if (prevParams === nextParams && JSON.stringify(this.props.query) === JSON.stringify(query) && this.props.hash === hash) {
      this.paramsChangedOnUpdate = false;
      return true;
    }

    this.paramsChangedOnUpdate = true;
    const {spinnerOnChange} = this.historyConfig;
    if (this.historyType === Sandbox.HISTORY_TYPES.SERVER && spinnerOnChange) {
      this.setState({spinner: true});
    }
    this.updateBasePath(nextProps);

    const {config} = nextProps;
    const pathPrefix = (config.sandbox || {}).pathPrefix || '';
    const path = pathPrefix + (/^\//.test(nextParams) ? nextParams : `/${nextParams}`);

    this.discoveryPending || this.sendHistoryMessage({
      type: Events.HISTORY,
      value: path + (nextProps.querystring ? `?${nextProps.querystring}` : '') + (hash ? `#${hash}` : '')
    });

    return true;
  }

  getSnapshotBeforeUpdate() {
    let snapshot = this.prevContext || null;
    this.prevContext = this.context;
    return snapshot;
  }

  hasConfigChanges(prevContext, keys) {
    return prevContext && keys.some(key => prevContext[key] !== this.context[key]);
  }

  componentDidUpdate(prevProps, prevState, prevContext) {
    // The path changed (& fired a history event in shouldComponentUpdate)
    // And Not Found was previously active.
    // We should hide Not Found since the page navigated and may not be needed now.
    this.paramsChangedOnUpdate && this.state.notFound && !this.state.noSource && this.setState({notFound: false});

    // Regenerate iframe source when IMS profile is known.
    // This is the case when discovery URL needs to be invoked.
    // Otherwise, dont need to do this since IMS profile isn't needed to start loading.
    if (this.waitingForContextUpdates && !this.shouldWaitForContextUpdates()) {
      this.waitingForContextUpdates = false;
      this.queuedDataEvents.forEach(fn => fn());
      this.queuedDataEvents = [];
      this.generateSourceOnReload();
      return;
    }

    // Don't send a configuration update if nothing has changed, or the src has not been set
    if (!this.state.src || JSON.stringify(prevContext) === JSON.stringify(this.context)) {
      return;
    }

    // Send event if Coach Marks position updated in Core state
    this.hasConfigChanges(prevContext, ['coachMarkPosition']) && this.sendMessage({
      type: Events.COACH_MARK_POSITIONED,
      value: this.context.coachMarkPosition
    });

    // Send the open/closed value of a global shell component to module runtime
    // When open, module runtime needs to hide the accessibility tree
    // i.e. Unified Nav or Global Search
    this.hasConfigChanges(prevContext, ['globalDialogOpen']) && this.sendMessage({
      type: Events.GLOBAL_DIALOG_OPEN,
      value: this.context.globalDialogOpen
    });

    // Send event if NPS score changed
    this.hasConfigChanges(prevContext, ['npsScore']) && this.sendMessage({
      type: Events.NPS,
      value: this.context.npsScore
    });

    const {refreshOnImsChange, spaAppId} = this.props.config;
    // If the feature flags have changed, determine if we should re-render the
    // app in case of SPA and AB testing version change.
    if (spaAppId && this.context.featureFlags && this.hasConfigChanges(prevContext, ['featureFlags'])) {
      const {ABTesting, liveVersion, spaName} = this.getSpaAppConfig(spaAppId);
      const srcVersion = this.state.src.searchParams.get(`${spaName}_version`);
      // If AB testing is enabled and the version of the SPA app that was
      // previously loaded is different from the newly generated one, update
      // the state's src property to reload the app.
      if (ABTesting && srcVersion !== liveVersion) {
        return this.generateIframeSource(true);
      }
    }

    // If refreshOnImsChange is set and IMS has changed, refresh iframe source.
    if (refreshOnImsChange && this.hasConfigChanges(prevContext, ['imsOrg'])) {
      return this.generateIframeSource(true);
    }
    this.sendConfiguration();
    this.context.customSearch.enabled && this.sendCustomSearchValue();
  }

  /**
   * Handles the case where data has been requested before it is available.
   * Ensures that any remaining requests for data are fulfilled.
   */
  processOpenRequests() {
    const updatedContext = Object.assign({}, this.lastPayload?.value || {}, this.context || {});
    Object.values(this.openRequests).forEach(value =>
      value.type in updatedContext && this.sendDataResponse(value));
  }

  /**
   * Constructs baseUrl using basePath.
   * @param {string} basePath The base path of the url.
   * @returns {string} The full base url.
   */
  setBaseUrl(basePath) {
    let baseUrl = new URL(window.location.origin);
    baseUrl.hash = basePath;
    baseUrl.search = window.location.search;
    return baseUrl.toString().replace(/\/$/, '');
  }

  getAnalyticsScript() {
    const {analytics} = this.props.config;
    const {code: solution, scripts = {}} = analytics || {};
    const env = this.getAppEnvironment();
    return scripts[env] ? {script: scripts[env], solution} : null;
  }

  async getConfigurePayload() {
    let {
      activeProductContext,
      appContainer,
      environment,
      featureFlags,
      imsClientId,
      imsEnvironment,
      imsInfo,
      imsOrg,
      imsOrgs,
      imsOrgName,
      imsProfile,
      imsToken,
      internal,
      locale,
      metricsEnv,
      preferredLanguages,
      sandbox: selectedSandbox,
      shellHeight,
      shellInfo,
      shellSideNavPresent,
      shellSideNavWidth,
      sideNavOpen,
      subOrg,
      tenant,
      theme,
      userConsent
    } = this.context;
    let {appId, gainsight, gqlEndpoint, sandbox, spaAppId} = this.props.config;
    const analytics = this.getAnalyticsScript();
    let baseUrl = this.setBaseUrl(this.basePath);
    const metricsConfig = await this.getMetricsConfig();

    return {
      type: Events.CONFIGURE,
      value: {
        activeProductContext,
        analytics,
        apiGatewayUrl: this.apiGatewayUrl,
        appContainer,
        appId,
        baseFrameUrl: this.configURL.toString(),
        baseUrl,
        discovery: this.state.discovery,
        environment,
        externalQueryParams: window.location.search,
        featureFlags,
        gainsight: {
          enabled: !!window.aptrinsic,
          productKey: 'AP-DFHK5XLSAWVI-2', // Default `exc` gainsight key, overridden by defined configs.
          shellPath: getPathWithoutTenant(window.location.hash.replace(/^#/, '')),
          user: {authId: imsProfile?.authId, permissions: userConsent},
          ...gainsight
        },
        gqlEndpoint,
        historyType: this.historyType,
        hosts: sandbox.hosts,
        imsClientId,
        imsEnvironment,
        imsInfo,
        imsOrg,
        imsOrgName,
        imsOrgs,
        imsProfile,
        imsToken,
        internal,
        ioGatewayUrl: this.ioGatewayUrl,
        ioRegionSpecificMap: this.ioRegionSpecificMap,
        locale,
        metricsAppId: this.metricsAppId,
        metricsConfig,
        metricsEnv,
        preferredLanguages,
        sandbox: selectedSandbox,
        shellHeight,
        shellInfo,
        shellSideNavCollapsed: !sideNavOpen,
        shellSideNavPresent,
        shellSideNavWidth,
        spaAppId,
        subOrg,
        tenant,
        theme: `spectrum--${theme}`,
        xqlGatewayUrl: this.xqlGatewayUrl
      }
    };
  }

  /**
   * This functon is handler for all events happening on the shell.
   * @param {Object} value The payload.
   */
  handleUserInputEvent = value => {
    const {target} = value;
    // The target object is too large to include in the post message
    delete value.target;
    // Handle a11y skip menu actions
    this.handleSkipMenuEvent(target, value);
    // Handle customFeedback clicks
    if (this.customFeedback && value.type === 'click' && (value.data || {}).className === 'spectrum-Shell-feedback-button') {
      this.sendMessage({type: Events.CUSTOM_FEEDBACK, value});
    }
    this.sendMessage({type: Events.USER_INPUT, value});
  }

  fetchSkipMenu = () => this.sendMessage({type: Events.SKIP_MENU_GET_LIST});

  /**
   * This functon handles all a11y skip menu events happening on the shell.
   * @param {Object} target The event target.
   * @param {Object} keyCode The event keyCode from the payload value.
   * @param {Object} type The event type from the payload value.
   */
  handleSkipMenuEvent = (target, {keyCode, type}) => {
    // Ensure this is a skip menu event. We check both
    // the target and it's parent in case it's a label
    // and the values we need are on the button instead
    /* eslint-disable-next-line max-len */
    const skipAction = target.closest('.skipMenu-listItem');
    // Early return is not a skip menu action
    if (!skipAction) {
      return;
    }
    // Get the data attribute for skip menu items
    const data = skipAction.getAttribute('data-skip-menu');
    // Send focus on skip menu click, space or enter keypress
    if (type === 'click' || (keyCode === 13 || keyCode === 32)) {
      if (data === 'fallback') {
        // Fall back to our iframe if the skip menu key is a fallback
        // This also handles the Safari case where we have to use the iframe
        // because Safari prevents programatic focusing
        // (https://github.com/braintree/braintree-web/issues/490)
        let main = document.querySelector('[id^="exc-app-sandbox-"]');
        main.tabIndex = -1;
        main.focus();
        main.addEventListener('blur', () => main.removeAttribute('tabIndex'), {once: true});
        return;
      } else {
        // Otherwise post a message with the skip item key
        return this.sendMessage({type: Events.SKIP_MENU_FOCUS, value: data});
      }
    }
    // Escape key removes focus on skip menu
    if (keyCode === 27) {
      document.activeElement.blur();
    }
  }

  onMount(frame) {
    this.frame = frame;
    this.sandboxTimer.time('init', {
      appId: this.metricsAppId,
      discovery: this.state.discovery,
      discoveryTime: this._discoveryTime
    });
    this.setState({loaded: true});
    // If somehow we got a ping before mount, process queue.
    if (this.connected) {
      this.metrics.event('Sandbox.mountAfterConnect');
      this.processMessageQueue();
    }
  }

  /**
   * Determines which base to use for the path based on whether the
   * `base` property is defined in the route config. This will be the default
   * instead of `this.basePath` unless it's not defined. This is for cases when
   * multiple configs are needed but need a single base. For example, Target has
   * 5 route configs (`/target/audiences`, etc) but really only want `/target`
   * to be the base.
   * @returns {string} The base path to use.
   */
  getBase() {
    const {base} = this.props.config;
    return base ? getUpdatedPath(this.basePath, base) : this.basePath;
  }

  /**
   * To be run on page load. Runs on every server history change.
   * @param {Object} obj The below params.
   * @param {string} obj.configuration Solution configuration.
   * @param {string} obj.hash Hash from iframe.
   * @param {string} obj.pathname Pathname of iframe.
   * @param {string} obj.search Search from iframe.
   */
  async onReady({hash, href, pathname, search}) {
    this.updateBasePath(this.props);

    let {router} = this.props;

    // In the "server" case (meaning, traditional server rendered app)
    // we want to redirect to the correct url if not already served on ready
    if (this.historyType === Sandbox.HISTORY_TYPES.SERVER) {
      // Get and combine paths and search strings
      let {absolutePaths} = this.historyConfig;
      let {browser, iframe} = getPaths(pathname, search, hash, !!absolutePaths, this.basePath, this.configURL);
      let browserString = browser.path + browser.search + browser.hash;
      let iframeString = iframe.path + iframe.search + iframe.hash;
      // Update metrics app ID if section has changed.
      // i.e. Analytics move between its SPA and PHP sections.
      if (this.sections) {
        const section = this.sections.find(sec => iframeString.startsWith(sec.route));
        if (section) {
          this.metricsAppId = section.metricsAppId || this.metricsAppId;
          this.prevSection = this.currentSection || '';
          this.currentSection = section.name;
          this.setMetricsApplication();
        }
      }
      // Compare the iframe's path with the current browser path
      if (hasChanges(browserString, iframeString).any) {
        // If not matching, update the browser's path to match iframe path.
        // Additionally, protect against multiple // in a row.
        router.trigger((this.basePath + iframeString).replace(/^\/{2,}/g, '/'), true);
      }
    }

    if (this.customIms && !await this.getToken(this.customIms)) {
      return;
    }

    this.sandboxTimer.time(this.initialized ? 'reconnected' : 'connected', `Solution loaded: ${this.metricsAppId}`, {
      appId: this.metricsAppId,
      customIms: this.customIms,
      hash,
      href,
      pathname,
      search
    });

    this.initialized = true;
    this.waitForReady = false;

    // Clear the page timeout
    clearTimeout(this.loadMessageTimeout);
    delete this.loadMessageTimeout;
    clearTimeout(this.loadTimeout);
    this.sendConfiguration(true);
  }

  /**
   * Only send configuration payloads if it's initialized and there is
   * a valid profile. If the app requests sandboxes, ensure that they are present.
   * @param {force} If force is true, we always send the configuration event.
   */
  shouldSendConfiguration(force) {
    const {imsProfile, sandbox} = this.context;
    const {urlContext} = this.props.config;
    const waitForSandboxes = urlContext?.key === 'sandbox' && !urlContext.optional && !sandbox;
    if (!force && !this.initialized || !imsProfile || this.configPending || waitForSandboxes) {
      // If solution is not yet ready but Shell is (not initialized and imsProfile available)
      // send a sendConfiguration event with solutionReady false.
      if (!this.initialized && !this.configPending && imsProfile && !waitForSandboxes) {
        this.sandboxTimer.time('sendConfiguration', {solutionReady: false});
      }
      return false;
    }
    return true;
  }

  async sendConfiguration(force = false) {
    // Check to see if there is any reason to delay sending the configuration
    if (!this.shouldSendConfiguration(force)) {
      return;
    }

    let imsData = {};

    if (this.customIms) {
      this.configPending = true;
      const res = await this.getToken(this.customIms);
      if (!res) {
        return;
      }
      const customImsInfo = Object.assign({}, this.context.imsInfo, {
        imsProfile: res.profile,
        imsToken: res.token
      });
      Object.assign(imsData, {
        imsClientId: this.customIms.client_id,
        imsInfo: customImsInfo,
        imsProfile: res.profile,
        imsToken: res.token
      });
    }

    let payload = await this.getConfigurePayload();
    Object.assign(payload.value, imsData);
    payload.changed = this.getChangedConfig(payload.value, (this.lastPayload || {}).value || {});
    const firstSend = !this.lastPayload;
    this.lastPayload = payload;
    this.sendMessage(payload);
    this.processOpenRequests();
    this.configPending = false;
    this.sandboxTimer.time('sendConfiguration', {firstSend, solutionReady: true});
  }

  sendCustomSearchValue() {
    let searchState = this.context.customSearch.open;
    // Only send event if the state of the search menu has changed
    if (searchState !== this.previousSearchState) {
      this.sendMessage({
        type: Events.CUSTOM_SEARCH,
        value: searchState
      });
      this.previousSearchState = searchState;
    }
  }

  getChangedConfig(payload, lastPayload) {
    // On first config event, mark all values as changed
    const keys = Object.keys(payload);
    if (!lastPayload) {
      return keys;
    }
    return keys.filter(key => JSON.stringify(payload[key]) !== JSON.stringify(lastPayload[key]));
  }

  /**
   * Gets a token from ims client id and scopes and sends to iframe.
   * @param {Object} ims
   * @param {string} ims.client_id Requested client id
   * @param {string} ims.scopes Requested ims scopes
   */
  async onCustomTokenRequest(value) {
    const {validClientIds = []} = this.props.config;
    const {data: ims} = value;

    // Ensure clientId is valid request
    if (validClientIds.includes(ims?.client_id)) {
      const {token} = await this.context.getToken(ims) || {};
      this.fulfillEvent(value, token, false, 'Error while fetching token');
    } else {
      this.rejectEvent(value, 'Requested client id is not supported');
    }
  }

  async getToken(ims) {
    const res = await this.context.getToken(ims);
    if (!res) {
      this.sandboxTimer.time('customTokenError', {appId: this.metricsAppId}, {level: Level.ERROR});
      this.setState({error: ErrorTypes.UNKNOWN});
    }
    return res;
  }

  async sendDataResponse(value) {
    const {id, type} = value;

    if (!this.lastPayload || !Object.keys(this.lastPayload).length) {
      this.openRequests[id] = value;
      return;
    }

    const CONTEXT_FETCHED = new Set(['sandbox', 'sandboxes']);
    this.metrics.event('Sandbox.sendDataResponse', {appId: this.metricsAppId, type});
    const updatedContext = Object.assign({}, this.lastPayload.value, this.context);
    this.openRequests[id] && delete this.openRequests[id];
    let response = type in updatedContext && updatedContext[type];

    // If the user has requested sandbox data wait for the response from
    // the context service.
    if (!response && CONTEXT_FETCHED.has(type)) {
      try {
        const values = await this.context.contextFetchPromise;
        response = values[type];
      } catch (error) {
        return this.rejectEvent(value, error);
      }
    }

    this.fulfillEvent(value, response, false, `${type} not found`);
  }

  processMessageQueue() {
    if (this.outQueue.length && this.connected && this.frame) {
      // Flush all outgoing messages now that we got a Ping.
      this.metrics.event('Sandbox.onMessage.queue.flush', {length: this.outQueue.length});
      this.outQueue.forEach(message => this.sendMessage(message));
      this.outQueue = [];
    }
  }

  sendMessage(msg) {
    // Send messages when we are connected and have the iframe reference.
    // Queue otherwise.
    if (this.connected && this.frame) {
      // Enhances security and defense against HTML injections on post messages.
      // Returns early if the origin is not one of the csp allowed origins.
      if (!this.validSourcesRegexString.test(this.origin)) {
        this.metrics.event('Sandbox.postOriginInvalid', 'Invalid origin', {level: Level.WARN}, {origin: this.origin});
        return;
      }
      if (this.frame.contentWindow) {
        this.frame.contentWindow.postMessage({...msg, unifiedShell: 1}, this.origin);
        return;
      }
      this.state.error || this.metrics.error('Sandbox frame has no contentWindow');
      return;
    }
    this.mounted && this.metrics.info(
      `${msg.type || 'unknown'} message sent before connection - Queueing.`, {
        connected: this.connected,
        hasFrame: !!this.frame
      });
    this.outQueue.push(msg);
  }

  onTitleChange(newTitle) {
    let divider = ' | ';
    let parts = document.title.split(divider);
    let end = parts[parts.length - 1];
    document.title = newTitle === end ? newTitle : `${newTitle}${divider}${end}`;
  }

  async getMetricsConfig() {
    const {environment, metricsEnv} = this.context;
    return Object.assign({},
      await Internal.configureMetrics(),
      {
        application: {
          id: this.metricsAppId
        },
        environment: metricsEnv || environment
      });
  }

  async onPing(message) {
    let {type, value} = message.data || {};
    const {appId} = this.props.config;
    // TODO: Second ping means iframe reloaded, need to restart timeout.
    this.connected && this.metrics.event('Sandbox.secondPing', {appId});
    this.connected = true;
    this.origin = message.origin;
    this.metrics.event('Sandbox.onMessage.ping', {appId, hasFrame: !!this.frame});
    // Reset timeout as we know the frame is alive.
    this.setPageTimeout();
    // Return empty message to initiate origin
    const metricsConfig = await this.getMetricsConfig();
    // Many SPAs load Runtime.js early but instantiate the Runtime object later.
    // For these scenarios, we send some useful information to the iframe via the PONG
    // event so that the iframe can do a few things ahead of Runtime instantiation such as
    // 1. Use EIM
    // 2. Preload resources (Currently only Brand SVGs)
    // PONG event is only dependant on the iframe loading Runtime.js
    this.sendMessage({appId, type: Events.PONG, value: {
      metricsConfig,
      preloads: {BrandIcons: {href: BrandIcons, resourceType: 'image'}}
    }});
    this.processMessageQueue();
    this.props.onMessage(type, value);
    return;
  }

  onMessage(ref, message) {
    let {appId, type, value, unifiedShell} = message.data || {};

    // Only accept messages from unified shell code
    if (unifiedShell !== 1) {
      return;
    }

    // If loading a third party solution, check if a supported event.
    if (this.thirdParty && !(type in EventsThirdParty)) {
      this.metrics.event(`Unsupported 3rd party event ${type}`);
      return;
    }

    if (type === Events.NOT_INITIALIZED) {
      this.waitForRuntime = true;
      this.metrics.event('Sandbox.waitingForRuntimeInit');
      return;
    }

    // Updates the origin to ensure it's the current one that is being used.
    // Analytics changes origins and post messaging won't work if we don't
    // update to the newest one.
    if (type === Events.PING) {
      this.onPing(message);
      return;
    }

    // Ignore events that come from a different iframe than what is currently loaded
    if (appId !== this.appId && appId !== 'unified-shell') {
      if (appId && this.lastAppId === appId) {
        this.metrics.info(
          `Post message received from previous app (${appId}). Current appId ${this.appId} - Ignoring.`,
          {message: message.data, origin: message.origin}
        );
        return;
      } else {
        this.metrics.info(
          `Post message appId ${appId} does not match current appId ${this.appId}`,
          {message: message.data, origin: message.origin}
        );
      }
    }

    // Handle each runtime event in the events queue
    if (type === Events.QUEUE) {
      let historyEvents = [];
      value = value.filter(v => {
        if (v[0] === Events.HISTORY) {
          historyEvents.push(v);
          return false;
        }
        return true;
      });

      if (historyEvents.length) {
        value.push(historyEvents[historyEvents.length - 1]);
      }

      // Ignore CLICK event if this is an OPEN_HELP_CENTER event
      if (value.find(v => v[0] === Events.OPEN_HELP_CENTER)) {
        value = value.filter(v => v[0] !== Events.CLICK);
      }

      let queueLen = value.length;
      const queueServiceTimer = this.metrics.start('SandboxQueueEventsTimer');
      value.forEach(([key, val]) => this.handlePostMessage(key, val));
      queueServiceTimer.time('done', `Sandbox Queue length: ${queueLen}`);
    }
  }

  unloadListener = e => {
    e.preventDefault();
    e.returnValue = this.unloadPromptMessage;
  }

  blockNavigation = (enabled, options) => {
    if (!enabled) {
      window.removeEventListener('beforeunload', this.unloadListener);
      this.unblock && this.unblock();
      return;
    }
    this.unblock = history.block((location, action) => {
      if (action !== 'REPLACE') {
        let promptUser = (options && options.onOrgChangeOnly) ?
          isOrgOrSubOrgChange(history.location.pathname, location.pathname) : true;
        if (promptUser) {
          return this.unloadPromptMessage;
        }
      }
    });
    window.addEventListener('beforeunload', this.unloadListener);
  }

  /**
   * Get app load performance data.
   * @param {string} iframePerformance - Serialized Iframe performance JSON
   * @returns {object} Performance data
   */
  getLoadInfo({iframePerformance = ''}) {
    const {getPerformanceMarks} = this.context;
    const firstRender = Sandbox.firstRender;
    Sandbox.firstRender = false;

    if (getPerformanceMarks) {
      return getPerformanceMarks({
        appDone: this.appDone,
        appId: this.metricsAppId,
        firstRender,
        iframePerformance,
        lastAppId: this.lastAppId,
        src: this.state.src,
        start: this.sandboxStart
      });
    }

    return {appId: this.metricsAppId, error: 'getPerformanceMarks not available'};
  }

  handlePostMessage(type, value) {
    switch (type) {
      case Events.APP_DATA:
        this.appApiHandler(value);
        break;
      case Events.AUTH_EXPIRATION:
        this.context.onSignOut(true, 'User timed out');
        break;
      case Events.BLOCK_NAVIGATION: {
        // Prevent navigation blocking for tenantless solutions since it is not supported
        const {hideTenant} = this.props.config;
        value && !hideTenant && this.blockNavigation(value.enabled, value.options);
        break;
      }
      case Events.CAPABILITY_DATA:
        this.capabilityApiHandler(value);
        break;
      case Events.CLICK:
        document.dispatchEvent(new CustomEvent('sandboxClick'));
        break;
      case Events.CLIPBOARD_WRITE:
        try {
          navigator.clipboard.writeText(value.msg);
        } catch {
          this.metrics.error(`Sandbox: handlePostMessage failed to write to clipboard ${value.msg}, appId: ${this.appId}`);
        }
        break;
      case Events.CUSTOM_HERO_CLICK:
        this.context.setConfigStates({customHeroClick: () => this.sendMessage({type: Events.HERO_CLICK})});
        break;
      case Events.CUSTOM_TOKEN:
        this.onCustomTokenRequest(value);
        break;
      case Events.DATA_REQUEST:
        this.sendDataResponse(value);
        break;
      case Events.DONE: {
        this.context.setConfigStates({solutionLoaded: true});
        this.setState({main: value.addMainElement, showWaitMessage: false, spinner: false});
        let loadInfo = this.getLoadInfo(value);
        if (this.currentSection) {
          loadInfo = Object.assign(loadInfo, {
            previousSection: this.prevSection,
            section: this.currentSection
          });
        }
        this.sandboxTimer.time({event: MetricsEvents.PAGE_LOAD_DONE}, this.metricsAppId, loadInfo);
        // For availability KPI, need an event that only fires once.
        this.appDone || this.metrics.event('Sandbox.appDone', {appId: this.metricsAppId});
        this.appDone = true;
        break;
      }
      case Events.EXC_PULSE:
        this.pulseHandler(value);
        break;
      case Events.EXC_SETTINGS:
        this.settingsHandler(value);
        break;
      case Events.FOCUS:
        document.dispatchEvent(new CustomEvent('sandboxClick'));
        break;
      case Events.HISTORY: {
        // Hash history modifies the provided path to include the hash as part
        // of the path. For example, if `value.path` is `#something`, the
        // updated value will be `/something`. This keeps the URL tidy.
        if (this.historyType === Sandbox.HISTORY_TYPES.HASH) {
          const url = new URL(`${window.location.origin}${value.path}`);
          value.path = `${url.pathname}${url.hash ? `/${hashToPath({hash: url.hash})}` : ''}`;
        }
        // Splits the pathname with the pathname in the config to ensure the
        // correct path is preserved on the URL. For example, a pathname of
        // `/dev/sc/page.php` with a config path of `/dev/sc` should net a
        // path of `/page.php` added onto the `basePath`.
        let topPath;
        if (this.historyConfig.absolutePaths) {
          topPath = `${this.basePath}${value.path}`;
        } else {
          topPath = `${this.basePath}${splitPath(value.path, this.configURL.pathname)}`;
        }
        // Only replace double slashes with a single if it's not after a :,
        // denoting that it's part of https://.
        topPath = topPath.replace(/([^:])\/{2,}/g, '$1/');
        updatePath({path: topPath}, true, true, this.context.contextKey);

        value.title && this.onTitleChange(value.title);
        break;
      }
      case Events.IFRAME_RELOAD:
        const {cacheBust} = value;
        this.metrics.event('Sandbox.iframeReload', {cacheBust});
        cacheBust ? this.generateIframeSource(true) : this.setState({frameKey: this.state.frameKey + 1});
        break;
      case Events.KEYPRESS:
        eventObserver.handleIframeKeyPress(value);
        break;
      case Events.NOT_FOUND:
        this.setState({notFound: true});
        break;
      case Events.OPEN_HELP_CENTER:
        document.dispatchEvent(new CustomEvent('openHelpCenter', {detail: value}));
        break;
      case Events.PERMISSIONS:
        this.permissionsHandler(value);
        break;
      case Events.READY:
        this.waitForReady = true;
        this.onReady(value);
        break;
      case Events.SHELL_REDIRECT: {
        const {discovery, path, replace = true} = value;
        discovery && (this.discoveryPending = true);
        this.props.router.trigger(getUpdatedPath(
          hashToPath({removePrecedingSlash: true}), path, this.props.config.hideTenant
        ), replace);
        this.discoveryPending = false;
        // The `discovery` flag was added as a workaround to an Analytics integration issue.
        // A better solution will soon follow (joao).
        discovery && this.generateIframeSource(true);
        break;
      }
      case Events.SESSION:
        this.sessionHandler(value);
        break;
      case Events.SHELL_SETTINGS:
        this.setProxiedConfiguration(value);
        break;
      case Events.SKIP_MENU_SET_LIST: {
        const {skipMenuList} = value;
        document.dispatchEvent(new CustomEvent('skipMenuList', {detail: skipMenuList || null}));
        break;
      }
      case Events.TITLE:
        this.onTitleChange(value.title);
        break;
      case Events.TRIGGER_NPS:
        document.dispatchEvent(new CustomEvent('triggerNps'));
        break;
      default:
        return;
    }
    this.props.onMessage(type, value);
  }

  /**
   * Queues incoming event if data is not yet available to fulfill it.
   * @param {boolean} condition - Condition needed to process right away
   * @param {function} handler - Method to process the event if its delayed (The callee)
   * @param {Object} value - Event data
   * @returns {boolean} true if queued, false otherwise.
   */
  queueEvent(condition, handler, value) {
    condition || this.queuedDataEvents.push(handler.bind(this, value));
    return !condition;
  }

  /**
   * Rejects an incoming data request event.
   * @param {Object} value - Event data
   * @param {string} error - Error description
   */
  rejectEvent(value, error) {
    if (error instanceof Error) {
      error = error.message;
    }

    this.sendMessage({
      type: Events.DATA_FULFILL,
      value: Object.assign(value, {error, rejected: true})
    });
  }

  /**
   * Fulfills an incoming data event with a provided response.
   * @param {Object} value - Event data
   * @param {Object} response - Response data
   * @param {boolean} allowEmpty - If false, empty response will trigger rejection
   * @param {string} errorMessage - Error to show if response is empty.
   */
  fulfillEvent(value, response, allowEmpty = true, errorMessage = 'Unknown error') {
    (response || allowEmpty) ?
      this.sendMessage({
        type: Events.DATA_FULFILL,
        value: Object.assign(value, {response})
      }) :
      this.rejectEvent(value, errorMessage);
  }

  /**
   * App api handler used to provide application metadata.
   * @param {Object} value - Event data, containing application name.
   */
  appApiHandler(value) {
    const {shellInfo} = this.context;
    const {data = ''} = value;

    if (!this.queueEvent(shellInfo, this.appApiHandler, value)) {
      const {landingpage, services, solutions} = shellInfo;
      const response = services.concat(solutions).concat(landingpage).find(l => l.appId === data);
      this.fulfillEvent(value, response, false, `${data} not found`);
    }
  }

  /**
   * Capability api handler used to provide capability metadata.
   * @param {string} value - Event data, containing capability id or name.
   */
  capabilityApiHandler(value) {
    const capability = this.context.getCapabilityWithIconUrl(value.data);
    this.fulfillEvent(value, capability, false, `${value.data} not found`);
  }

  /**
   * Settings handler to read/write/delete settings.
   * @param {Object} value Params and required Keys and settings.
   * @returns {Promise} Settings response.
   */
  async settingsHandler(value) {
    const {imsProfile, settingsService} = this.context;
    if (!this.queueEvent(imsProfile && settingsService, this.settingsHandler, value)) {
      this.metrics.info(`Sandbox: SettingsHandler received request for ${value.id}, appId: ${this.appId}`);
      try {
        const response = await settingsService.settingsHandler(value.data, value.method);
        this.metrics.info(`Sandbox: SettingsHandler received response for ${value.id}, appId: ${this.appId}`);
        this.fulfillEvent(value, response);
      } catch {
        this.metrics.info(`Sandbox: SettingsHandler failed for ${value.id}, appId: ${this.appId}`);
        this.rejectEvent(value, 'Settings call failed');
      }
    }
  }

  async sessionHandler(value) {
    const {discoveryService, imsOrg, imsProfile, sessionService} = this.context;
    const {data: session, method} = value;
    const {session: sessionConfig} = this.props.config;

    if (!sessionConfig) {
      return this.rejectEvent(value, 'Session not configured');
    }

    if (!this.queueEvent(discoveryService && sessionService && imsProfile && imsOrg, this.sessionHandler, value)) {
      try {
        let response = {};
        const options = this.getDiscoveryOptions();
        switch (method) {
          case 'DELETE':
            await sessionService.invalidateSession(session, this.context, options);
            break;
          case 'GET': {
            const src = this.getSourceEnv();
            const source = typeof src === 'object' ? src : undefined;
            response = await sessionService.getSession(this.context, options, source, discoveryService);
            break;
          }
          case 'POST':
            await sessionService.setSession(session, this.context, options);
            break;
        }
        this.fulfillEvent(value, response);
      } catch (err) {
        this.rejectEvent(value, err);
      }
    }
  }

  /**
   * Permissions handler to read permissions.
   * @param {Object} value Required permissions.
   * @returns {Promise} Permissions response.
   */
  async permissionsHandler(value) {
    const {imsProfile, imsOrg, permissionService, sandbox, sandboxesAvailable} = this.context;

    if (this.props.config?.urlContext?.key !== 'sandbox' || !sandboxesAvailable) {
      return this.rejectEvent(
        value,
        'Permissions Service is only available to apps and orgs with PALM sandboxes enabled'
      );
    }

    if (!this.queueEvent(imsProfile && permissionService && sandbox, this.permissionsHandler, value)) {
      try {
        const response = await permissionService.get(value.data, imsOrg, sandbox);
        this.metrics.info(`Sandbox: PermissionsHandler received response for ${value.id}, appId: ${this.appId}`);
        this.fulfillEvent(value, response);
      } catch {
        this.metrics.error(`Sandbox: PermissionsHandler failed to get response for ${value.id}, appId: ${this.appId}`);
        this.rejectEvent(value, 'Unable to fetch permissions');
      }
    }
  }

  /**
   * Pulse handler for sending notifications.
   * @param {Object} value - Notification data.
   */
  async pulseHandler(value) {
    const {imsProfile, imsOrg} = this.context;
    if (!this.queueEvent(imsProfile && imsOrg, this.pulseHandler, value)) {
      try {
        const pulseService = await this.getPulseService();
        const response = await pulseService.sendNotifications({
          appId: this.appId,
          data: value.data,
          imsOrgId: imsOrg,
          method: value.method,
          userProfile: imsProfile
        });
        this.fulfillEvent(value, response);
      } catch (err) {
        this.metrics.error(
          'Sandbox: PulseHandler failed to send notifications',
          {error: err.message || err.toString()}
        );
        this.rejectEvent(value, 'Failed to send notifications');
      }
    }
  }

  setProxiedConfiguration(config) {
    // if digitalData is present, convert back to object before sending to metrics
    config.digitalData && (config.digitalData = JSON.parse(config.digitalData));
    this.metrics.event('Sandbox.setProxiedConfiguration', config);
    const validKeys = new Set([
      'appContainer',
      'coachMark',
      'customEnvLabel',
      'customSearch',
      'digitalData',
      'helpCenter',
      'hero',
      'modal',
      'nps',
      'onCustomButtonClick',
      'showLanguagePicker',
      'sidenav/visible',
      'sidenav/collapsed',
      'sidenav/config',
      'solution'
    ]);
    Object.keys(config).forEach(key => {
      let value = config[key];
      switch (key) {
        case 'customButtons':
          config.onCustomButtonClick = clickValue => this.sendMessage({
            type: scopedEvents[clickValue.scope],
            value: clickValue
          });
          break;
        case 'feedback':
          if (value.type === 'custom') {
            this.customFeedback = true;
          }
          break;
        case 'logoutUrl':
          this.context.setLogoutUrl(value);
          break;
        case 'preventDefaultCombos':
          eventObserver.setPreventDefaultCombos(value);
          break;
        case 'sidebar':
          // This is now deprecated in favor of the sidenav config, which should be used instead.
          // Remove this once all solutions have been transitioned to sidenav.
          value.config && value.config.menu && this.cleanSidenavUrls(value.config.menu);
          break;
        case 'sidenav/config':
          value && value.menu && this.cleanSidenavUrls(value.menu);
          break;
        case 'solution':
          config.hero = value;
          break;
        case 'spinner':
          this.metrics.event(value ? MetricsEvents.SPINNER_START : MetricsEvents.SPINNER_DONE);
          this.setState({showWaitMessage: false, spinner: value});
          break;
        case 'subOrgs':
          this.metrics.info(`subOrgs set by solution ${this.metricsAppId}`);
          break;
        case 'title':
          document.title = value;
          break;
        case 'unloadPromptMessage':
          this.unloadPromptMessage = value;
          break;
        case 'workspaces':
          value.map(val => this.updateWorkspaceURL(val));
          break;
        default: {
          if (!validKeys.has(key)) {
            // Delete any keys that do not match the above keys or keys in valid set so
            // Core state isn't filled with unneeded states and states don't get overridden
            delete config[key];
            this.metrics.info(`Sandbox: unknown shell configuration ${key}`);
          }
        }
      }
    });
    this.context.setConfigStates(config);
  }

  cleanUrl = url => {
    if (!url) {
      return '';
    }
    return /^https?:/.test(url) ? url : getPathWithoutTenant(`${this.getBase()}${url}`);
  };

  cleanSidenavUrls(menu) {
    for (let item of menu) {
      if (item.url) {
        item.url = item.absolutePath ? item.url : this.cleanUrl(item.url);
      } else if (item.children) { // nested children items
        this.cleanSidenavUrls(item.children);
      } else if (item.items) { // items nested in headers
        this.cleanSidenavUrls(item.items);
      }
    }
  }

  updateWorkspaceURL(workspace, parent) {
    const generateUid = (name, parentUid) => parentUid ? `${parentUid}_${name}` : name;

    if (!workspace.menu) {
      workspace.uid = generateUid(workspace.name, (parent || {}).uid);
      workspace.url = this.cleanUrl(workspace.url);
      return;
    }

    workspace.uid = generateUid(workspace.name, (parent || {}).uid);
    // Ensure that the workspace url is set to at least an empty string. Not
    // setting the url tells us that the workspace should not be added to the
    // workspace menu as the first item.
    workspace.url = this.cleanUrl(workspace.url);

    workspace.menu.forEach(ws => {
      ws.uid = generateUid(ws.name, workspace.uid);
      ws.url = this.cleanUrl(ws.url);
      ws.menu && this.updateWorkspaceURL(ws, workspace);
    });

    // Clicking on the top-level workspace opens the menu. If the workspace url
    // is set, we add it as the first item in the menu so that it can be
    // navigated to.
    workspace.url && workspace.menu.unshift({
      name: workspace.name,
      uid: generateUid(workspace.name, workspace.uid),
      url: workspace.url
    });
  }

  render() {
    const spinnerFrag = (
      <React.Fragment>
        <div className="spinner-overlay" />
        <ShellWait className="spinner-light" locale={this.context.locale} />
      </React.Fragment>
    );
    let {error, main, noSource, notFound, spinner, src} = this.state;
    if (error) {
      this.sandboxTimer.time('render', {message: error});
      return (
        <React.Suspense fallback={spinnerFrag}>
          <ErrorView type={error} />
        </React.Suspense>
      );
    }

    spinner = !src || this.context.htmlReady === HTMLStatus.PENDING || spinner;

    let name = this.context.formatMessage({
      customLocale: this.context.locale,
      defaultMessage: 'Main Content',
      messageId: 'solutionIframe'
    });

    let sandboxProps = main ? {role: 'main'} : {};
    this.sandboxTimer.time('render', {appId: this.metricsAppId, main, spinner, src});
    const showSpinner = !notFound && spinner;
    const permissionsPolicy = `fullscreen;${(this.props.config.permissionsPolicy || []).join(';')}`;

    return (
      <div className={classNames('exc-core-sandbox', {appContainer: this.context.appContainer})} {...sandboxProps}>
        {notFound &&
          <React.Suspense fallback={spinnerFrag}>
            <ErrorView type={noSource ? ErrorTypes.UNKNOWN : ErrorTypes.NOT_FOUND} />
          </React.Suspense>}
        <Iframe
          appId={this.appId}
          environment={this.context.environment}
          frameKey={this.state.frameKey}
          name={name}
          onLoad={this.props.onLoad}
          onMessage={this.onMessage}
          onMount={this.onMount}
          permissionsPolicy={permissionsPolicy}
          src={src ? src.toString() : ''}
          style={{visiblity: this.state.loaded ? 'visible' : 'hidden'}}
          timeout={20000}
          title={name}
          validSourcesRegexString={this.validSourcesRegexString}
        />
        {showSpinner && spinnerFrag}
        {showSpinner && this.state.showWaitMessage && (
          <React.Suspense fallback={null}>
            <Heading className="still-waiting" variant="subtitle1">
              {this.context.formatMessage({
                customLocale: this.context.locale,
                defaultMessage: 'Thank you for waiting. Just a few more moments...',
                messageId: 'iframeWaitingMessage'
              })}
            </Heading>
          </React.Suspense>
        )}
      </div>
    );
  }
}

Sandbox.contextType = CoreContext;

Sandbox.propTypes = {
  config: PropTypes.object.isRequired,
  onLoad: PropTypes.func,
  onMessage: PropTypes.func
};

Sandbox.defaultProps = {
  onLoad: function () {},
  onMessage: function () {}
};

Sandbox.HISTORY_TYPES = {
  HASH: 'HASH',
  HISTORY: 'HISTORY',
  SERVER: 'SERVER'
};
